import React, { useCallback, useEffect } from "react";
import PullToRefresh from "pulltorefreshjs";
import { useSelector } from "react-redux";
import { PulseLoader } from "react-spinners";
import { useNavigate } from "react-router";
import { DashboardConfigWidget, IGetTimeOffBalance } from "../../../types";
import { renderMobileDashboardWidget } from "../../../config";
import { RootState, useAppDispatch } from "../../../redux/store";
import { WebQuickActionsTile } from "../../../components/web/webDashboard";
import { getBalanceByCategory, getTimeOffRequestList } from "../../../redux/features/timeoff";
import { setShowTopNav, setTopNavNameTitle } from "../../../redux/features/mobileLayout";
import { getMyTodaysSchedule } from "../../../redux/features/schedule/ScheduleApi";
import { fetchDashboardWidgetConfig } from "../../../redux/features/dashboard";
import { getActionRequiredCount } from "../../../redux/features/mobileNotifications";
import { DateTimeUtil, getPrimaryActorId } from "../../../utils";
import { getWhoIsWorking } from "../../../redux/features/whoIsIn";
import { clearGlobalBackHandler } from "../../../hooks";
import { isPrivilege, UserPrivileges } from "../../../rules";
import {
    ACTION_REQUIRED_DASHBOARD_WIDGET_CODES,
    DEFAULT_DASHBOARD_WIDGET_ORDER,
    DEFAULT_DASHBOARD_WIDGET_VISIBILITY,
    ensureActionRequiredDashboardWidget,
    getDashboardDateRange,
    isAnyDashboardWidgetEnabled,
    isDashboardWidgetEnabled,
} from "../../../utils/dashboardWidgets";

const DASHBOARD_LOADING_MESSAGES = [
    "Your dashboard is being prepared...",
    "Fetching your account details...",
    "Almost ready...",
];

const MobileDashboard: React.FC = () => {
    const user = useSelector((state: RootState) => state.user.user);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const isAccountOwner = user.is_account_owner;
    const isManager = user.is_manager;

    const { employees: whoIsWorkingEmp, isLoading: whoIsWorkingLoading } = useSelector(
        (state: RootState) => state.whoIsIn
    );
    const { dashboardConfigWidgets, dashboardConfigCustomLinks, dashboardConfigLoadedForUserId, dashboardConfigError } = useSelector(
        (state: RootState) => state.dashboard
    );
    const { actionRequiredCount, isActionRequiredCountLoading } = useSelector(
        (state: RootState) => state.mobileNotifications
    );
    const [hasCompletedDashboardConfigRequest, setHasCompletedDashboardConfigRequest] = React.useState(false);
    const [skipMealBreakRefreshKey, setSkipMealBreakRefreshKey] = React.useState(0);
    const refreshContainerRef = React.useRef<HTMLDivElement | null>(null);
    const dashboardUserId = getPrimaryActorId(user);
    const currentUserId = dashboardUserId != null && dashboardUserId !== -1 ? String(dashboardUserId) : null;
    const loadedDashboardConfigUserId = dashboardConfigLoadedForUserId != null
        ? String(dashboardConfigLoadedForUserId)
        : null;
    const hasResolvedDashboardConfig = Boolean(
        currentUserId && loadedDashboardConfigUserId === currentUserId
    );
    const shouldUseDashboardConfig = hasResolvedDashboardConfig && !dashboardConfigError;
    const configuredDashboardWidgets = shouldUseDashboardConfig
        ? dashboardConfigWidgets
        : DEFAULT_DASHBOARD_WIDGET_ORDER;
    const orderedDashboardWidgets = ensureActionRequiredDashboardWidget(configuredDashboardWidgets);
    const customLinks = shouldUseDashboardConfig ? dashboardConfigCustomLinks : [];
    const shouldWaitForDashboardConfig = Boolean(currentUserId && !hasCompletedDashboardConfigRequest);
    const isDashboardLoading = !currentUserId || shouldWaitForDashboardConfig;
    const shouldFetchWhoIsIn = isDashboardWidgetEnabled(
        orderedDashboardWidgets,
        shouldUseDashboardConfig,
        "who_is_in",
        DEFAULT_DASHBOARD_WIDGET_VISIBILITY.WHO_IS_IN
    );
    const shouldFetchMySchedule = isDashboardWidgetEnabled(
        orderedDashboardWidgets,
        shouldUseDashboardConfig,
        "my_schedule",
        DEFAULT_DASHBOARD_WIDGET_VISIBILITY.MY_SCHEDULE
    );
    const shouldFetchTimeOff = isDashboardWidgetEnabled(
        orderedDashboardWidgets,
        shouldUseDashboardConfig,
        "module_timeoff",
        DEFAULT_DASHBOARD_WIDGET_VISIBILITY.MODULE_TIMEOFF
    );
    const shouldFetchActionRequired = isAnyDashboardWidgetEnabled(
        orderedDashboardWidgets,
        shouldUseDashboardConfig,
        ACTION_REQUIRED_DASHBOARD_WIDGET_CODES,
        DEFAULT_DASHBOARD_WIDGET_VISIBILITY.ACTION_REQUIRED
    );

    const userPrivileges = user.employee_access_role_privileges;
    const isAdmin = isPrivilege({
        privileges: userPrivileges,
        rule: UserPrivileges.EMPLOYEE_PRIV_ADMIN,
    });
    const hasScheduleToolPrivilege =
        isPrivilege({
            privileges: userPrivileges,
            rule: UserPrivileges.EMPLOYEE_PRIV_SCHEDULE_TOOL,
        }) || isAdmin;

    const fetchInitialDashboardData = useCallback(async (forceRefresh: boolean = false) => {
        const promises: Promise<any>[] = [];

        if (shouldFetchWhoIsIn) {
            promises.push(
                dispatch(
                    getWhoIsWorking({
                        current_datetime: DateTimeUtil.formatDateToMySQL(new Date()),
                        forceRefresh,
                    })
                )
            );
        }

        if (shouldFetchMySchedule) {
            promises.push(
                dispatch(
                    getMyTodaysSchedule({
                        forceRefresh,
                    })
                )
            );
        }

        if (dashboardUserId != null && dashboardUserId !== -1 && shouldFetchTimeOff) {
            const body: IGetTimeOffBalance = {
                category_code: "",
                employee_id: String(dashboardUserId),
                forceRefresh,
            };

            const dateRange = getDashboardDateRange();

            const getTimeOffRequestsBody = {
                start_date: dateRange[0],
                end_date: dateRange[dateRange.length - 1],
                employee_id: String(dashboardUserId),
                category_code: "",
                forceRefresh,
            };

            promises.push(
                dispatch(getBalanceByCategory(body)),
                dispatch(getTimeOffRequestList(getTimeOffRequestsBody))
            );
        }

        if (shouldFetchActionRequired) {
            promises.push(dispatch(getActionRequiredCount({ forceRefresh })));
        }

        await Promise.all(promises);
    }, [dashboardUserId, dispatch, shouldFetchActionRequired, shouldFetchMySchedule, shouldFetchTimeOff, shouldFetchWhoIsIn]);

    const refreshData = useCallback(async () => {
        setSkipMealBreakRefreshKey((current) => current + 1);
        await fetchInitialDashboardData(true);
    }, [fetchInitialDashboardData]);

    useEffect(() => {
        if (currentUserId && hasCompletedDashboardConfigRequest) {
            fetchInitialDashboardData(false);
        }
    }, [currentUserId, fetchInitialDashboardData, hasCompletedDashboardConfigRequest]);

    useEffect(() => {
        if (!currentUserId) {
            setHasCompletedDashboardConfigRequest(false);
            return;
        }

        let isActive = true;

        const loadDashboardConfig = async () => {
            setHasCompletedDashboardConfigRequest(false);
            await dispatch(fetchDashboardWidgetConfig());

            if (isActive) {
                setHasCompletedDashboardConfigRequest(true);
            }
        };

        loadDashboardConfig();

        return () => {
            isActive = false;
        };
    }, [currentUserId, dispatch]);

    useEffect(() => {
        if (isDashboardLoading) {
            return () => {
                dispatch(setShowTopNav(true));
            };
        }

        let isCancelled = false;
        let frameId = 0;

        frameId = window.requestAnimationFrame(() => {
            if (isCancelled) {
                return;
            }

            const container = refreshContainerRef.current;
            if (!container || !container.isConnected || !container.parentNode) {
                return;
            }

            PullToRefresh.destroyAll();
            PullToRefresh.init({
                // The runtime library accepts live elements, even though its TS types only declare strings.
                mainElement: container,
                triggerElement: container,
                onRefresh: () => refreshData(),
                instructionsPullToRefresh: "Pull to refresh",
                instructionsReleaseToRefresh: "Release to refresh",
                getMarkup: () => `
                    <div class="w-full h-16 flex items-center justify-center bg-white shadow-sm">
                        <div class="w-full flex items-center justify-center">
                        <div class="flex items-center justify-center gap-2 text-sm text-gray-600">
                            <div class="h-4 w-4 border-2 border-t-transparent border-gray-500 rounded-full animate-spin"></div>
                            <span>Refreshing...</span>
                        </div>
                        </div>
                    </div>
                    `,
                distThreshold: 40,
                distMax: 60,
                distReload: 50,
                shouldPullToRefresh() {
                    return container.scrollTop === 0;
                },
            } as any);
        });

        return () => {
            isCancelled = true;
            window.cancelAnimationFrame(frameId);
            PullToRefresh.destroyAll();
            dispatch(setShowTopNav(true));
        };
    }, [dispatch, isDashboardLoading, refreshData]);

    const [loadingMessage, setLoadingMessage] = React.useState(0);

    useEffect(() => {
        if (isDashboardLoading) {
            const interval = setInterval(() => {
                setLoadingMessage((prev) => (prev + 1) % DASHBOARD_LOADING_MESSAGES.length);
            }, 1500);

            return () => clearInterval(interval);
        }
    }, [isDashboardLoading]);

    if (isDashboardLoading) {
        return (
            <div className="flex flex-col items-center justify-center h-screen w-full bg-white">
                <PulseLoader
                    color="#3B82F6"
                    loading={true}
                    size={12}
                    speedMultiplier={0.8}
                    margin={4}
                    aria-label="Loading Dashboard"
                />
                <div
                    className="mt-6 text-gray-600 text-center text-base font-light"
                    style={{
                        animation: "fadeIn 0.3s ease-out",
                    }}
                >
                    {DASHBOARD_LOADING_MESSAGES[loadingMessage]}
                </div>
                <style>{`
                    @keyframes fadeIn {
                        from { opacity: 0; transform: translateY(5px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                `}</style>
            </div>
        );
    }

    const workScheduleAndAvailabilityOnPress = () => {
        dispatch(setTopNavNameTitle("My Schedule"));
        navigate("/m-calendar");
    };

    const isWidgetVisible = (widget: DashboardConfigWidget, fallbackVisibility?: boolean): boolean => {
        return isDashboardWidgetEnabled(
            orderedDashboardWidgets,
            shouldUseDashboardConfig,
            widget.widget_code,
            fallbackVisibility
        );
    };

    return (
        <div ref={refreshContainerRef} id="refresh-container" className="h-full overflow-y-auto bg-white">
            <div className="p-4 space-y-6">
                {orderedDashboardWidgets.map((widget, index) => {
                    const renderedWidget = renderMobileDashboardWidget({
                        widget,
                        isWidgetVisible,
                        skipMealBreakRefreshKey,
                        actionRequiredCount,
                        isActionRequiredCountLoading,
                        whoIsWorkingEmp: whoIsWorkingEmp || [],
                        whoIsWorkingLoading: whoIsWorkingLoading || false,
                        WHO_IS_IN: DEFAULT_DASHBOARD_WIDGET_VISIBILITY.WHO_IS_IN,
                        TIMECARD: DEFAULT_DASHBOARD_WIDGET_VISIBILITY.TIMECARD,
                        MODULE_TIMEOFF: DEFAULT_DASHBOARD_WIDGET_VISIBILITY.MODULE_TIMEOFF,
                        MY_SCHEDULE: DEFAULT_DASHBOARD_WIDGET_VISIBILITY.MY_SCHEDULE,
                        REMOTE_WORK_COUNTER: DEFAULT_DASHBOARD_WIDGET_VISIBILITY.REMOTE_WORK_COUNTER,
                        PARIS_WORK_COUNTER: DEFAULT_DASHBOARD_WIDGET_VISIBILITY.PARIS_WORK_COUNTER,
                        canShowTeamTimesheet: Boolean(isManager || isAccountOwner),
                        canShowShiftPlanner: Boolean(hasScheduleToolPrivilege),
                        onOpenWidgetUrl: (url: string) => {
                            navigate(url);
                        },
                        onOpenSchedule: workScheduleAndAvailabilityOnPress,
                        onOpenTeamTimesheet: () => {
                            clearGlobalBackHandler();
                            navigate("/m-manager-timesheet", { state: { from: "/mobile-dashboard" } });
                        },
                        onOpenShiftPlanner: () => {
                            navigate("/scheduling-tool");
                        },
                        onOpenTimeOff: () => {
                            navigate("/m-timeoff", { state: { from: "/mobile-dashboard" } });
                        },
                        onOpenNotifications: () => {
                            navigate("/m-notifications");
                        },
                    });

                    if (!renderedWidget) {
                        return null;
                    }

                    return (
                        <React.Fragment key={`${widget.widget_code || "dashboard-widget"}-${widget.sort_order || index}`}>
                            {renderedWidget}
                        </React.Fragment>
                    );
                })}

                {customLinks.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                        <WebQuickActionsTile links={customLinks} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default MobileDashboard;
